import { ICreatePlanification } from '@sdk/contracts';
import { Form, Checkbox, DatePicker, Flex, App } from 'antd';
import dayjs from 'dayjs';
import { useState, forwardRef, useImperativeHandle, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SpecificDaysPicker } from '../RestDays/SpecificDaysPicker/SpecificDaysPicker';
import { WeekDaysPicker } from '../RestDays/WeekDaysPicker/WeekDaysPicker';

import { StyledCheckbox, Wrapper } from './PlanificationForm.styles';
import { PlanificationResult } from './PlanificationResult';

import { usePlanificationStates } from '@/hooks';
import { useMutatePlanification, useMySubscriptions, useProfile } from '@/store';
import { formatSecondsInHoursAndMinutes } from '@/utils';

interface PlanificationFormProps {
  onReadyToCalculateChange?: (isReady: boolean) => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const PlanificationForm = forwardRef<{ submit: () => void }, PlanificationFormProps>(
  ({ onReadyToCalculateChange, onLoadingChange }, ref) => {
    const { t } = useTranslation('DefaultTranslations');
    const { profile } = useProfile();
    const { planificableSubscriptions } = useMySubscriptions();
    const { createPlanification, isWorking } = useMutatePlanification();
    const [form] = Form.useForm();
    const { showPlanificationForm, setShowPlanificationForm, setShowPlanificationViewer } =
      usePlanificationStates();
    const [dailyStudyTime, setDailyStudyTime] = useState(0);
    const { hours, minutes } = useMemo(
      () => formatSecondsInHoursAndMinutes(dailyStudyTime),
      [dailyStudyTime]
    );

    useImperativeHandle(ref, () => ({
      submit: () => form.submit()
    }));

    useEffect(() => {
      onLoadingChange?.(isWorking);
    }, [isWorking, onLoadingChange]);

    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [endDate, setEndDate] = useState<Date>();

    const { message } = App.useApp();

    const handleCalculate = (values: ICreatePlanification) => {
      if (!profile?._id) return;

      const payload: ICreatePlanification = {
        ...values,
        userId: profile._id,
        endDate: values.endDate ? new Date(values.endDate) : undefined,
        restDates: (values.restDates ?? []).map((date: Date) => new Date(date)),
        restWeekdays: values.restWeekdays ?? [],
        includeAllActivities: true
      };

      createPlanification(payload, {
        onSuccess: data => {
          setDailyStudyTime(data.dailyStudyTime);
          setEndDate(data.endDate);
          setResultModalOpen(true);
        }
      });
    };

    const handleSave = () => {
      setResultModalOpen(false);
      setShowPlanificationForm(false);
      message.success(t('CHANGES_SAVED'));
    };

    const handleBack = () => {
      setResultModalOpen(false);
      setShowPlanificationForm(true);
      setShowPlanificationViewer(false);
    };

    return (
      <Wrapper>
        {showPlanificationForm && (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, allValues) => {
              const hasEndDate = Boolean(allValues?.endDate);
              const hasSubscriptions = Array.isArray(allValues?.subscriptions)
                ? allValues.subscriptions.length > 0
                : false;
              const isReady = hasEndDate && hasSubscriptions;
              onReadyToCalculateChange?.(isReady);
            }}
            onFinish={handleCalculate}>
            <Form.Item
              name="endDate"
              label={t('FINISH_DATE_PLANIFICATION_QUESTION')}
              rules={[{ required: true, message: t('SELECT_A_DATE') }]}>
              <DatePicker width={'100%'} format="DD-MM-YYYY" minDate={dayjs().startOf('day')} />
            </Form.Item>
            <Form.Item name="restWeekdays" label={t('WHAT_ARE_YOUR_REST_DAYS')}>
              <WeekDaysPicker />
            </Form.Item>
            <Form.Item name="restDates" label={t('WHAT_ARE_YOUR_REST_DATES')}>
              <SpecificDaysPicker />
            </Form.Item>
            <Form.Item
              name="subscriptions"
              label={t('CHOOSE_THE_COURSES_YOU_WILL_STUDY')}
              rules={[{ required: true, message: t('SELECT_AT_LEAST_ONE_COURSE') }]}>
              <Checkbox.Group>
                <Flex vertical gap={8}>
                  {planificableSubscriptions?.map(s => (
                    <StyledCheckbox key={s._id} value={s._id}>
                      <span style={{ marginRight: '8px' }}>{s.course?.emoji}</span>
                      <span>{s.course?.name}</span>
                    </StyledCheckbox>
                  ))}
                </Flex>
              </Checkbox.Group>
            </Form.Item>
          </Form>
        )}
        <PlanificationResult
          resultModalOpen={resultModalOpen}
          planificationHours={hours}
          planificationMinutes={minutes}
          planificationEndDate={endDate}
          handleSave={handleSave}
          handleBack={handleBack}
        />
      </Wrapper>
    );
  }
);
